import { client } from '@/client';
import {
  CreateLongLivedTokenRequest,
  SaveTokenResponse,
  TokensListResponse,
  UpdateLongLivedTokenRequest,
} from '@/client/long-lived-token';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { GenericForbiddenApiError } from './types';

const queryKey = 'long-lived-tokens';

export const useLongLivedTokens = (
  params: UseQueryOptions & {
    accountId: string;
  },
): UseQueryResult<TokensListResponse> & {
  longLivedTokens: TokensListResponse | undefined;
} => {
  const {
    accountId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    [queryKey, accountId],
    () =>
      client.longLivedToken.getTokens(accountId, {
        sort: [['created', 'desc'].join(',')],
      }),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!accountId,
      ...(restOptions as any),
    },
  );

  return {
    longLivedTokens: data,
    ...(rest as UseQueryResult<TokensListResponse>),
  };
};

export const useCreateLongLivedToken = (): {
  create: (
    accountId: string,
    payload: CreateLongLivedTokenRequest,
  ) => Promise<SaveTokenResponse>;
} & {
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const { mutateAsync, isLoading, error } = useMutation<
    SaveTokenResponse,
    GenericForbiddenApiError,
    CreateLongLivedTokenRequest
  >('saveLongLivedToken', (payload: CreateLongLivedTokenRequest) =>
    client.longLivedToken.createToken(payload.accountId, payload),
  );

  const create = (accountId: string, payload: CreateLongLivedTokenRequest) =>
    mutateAsync({ ...payload, accountId });

  return {
    create,
    isLoading,
    error: error as GenericForbiddenApiError | null,
  };
};

export const useUpdateLongLivedToken = (): {
  update: (
    accountId: string,
    tokenId: string,
    payload: UpdateLongLivedTokenRequest,
  ) => Promise<SaveTokenResponse>;
} & {
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const { mutateAsync, isLoading, error } = useMutation<
    SaveTokenResponse,
    GenericForbiddenApiError,
    { accountId: string; tokenId: string; payload: UpdateLongLivedTokenRequest }
  >('updateLongLivedToken', ({ accountId, tokenId, payload }) =>
    client.longLivedToken.updateToken(accountId, tokenId, payload),
  );

  const update = (
    accountId: string,
    tokenId: string,
    payload: UpdateLongLivedTokenRequest,
  ) => mutateAsync({ accountId, tokenId, payload });

  return {
    update,
    isLoading,
    error: error as GenericForbiddenApiError | null,
  };
};

export const useDeleteLongLivedToken = (): {
  deleteToken: (accountId: string, tokenId: string) => Promise<void>;
} & {
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const { mutateAsync, isLoading, error } = useMutation<
    void,
    GenericForbiddenApiError,
    { accountId: string; tokenId: string }
  >('deleteLongLivedToken', ({ accountId, tokenId }) =>
    client.longLivedToken.deleteToken(accountId, tokenId),
  );

  const deleteToken = (accountId: string, tokenId: string) =>
    mutateAsync({ accountId, tokenId });

  return {
    deleteToken,
    isLoading,
    error: error as GenericForbiddenApiError | null,
  };
};
